import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Receipt, FileText, AlertTriangle, Save, Loader2, Star, Settings, Eraser } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { InutilizacoesTab } from './InutilizacoesTab';

type Tipo = 'nfce' | 'nfe' | 'avaliacao' | 'config' | 'inutil';

const ALIQUOTA_PADRAO = 15;

interface AvaliacaoConfig {
  ativo: boolean;
  link: string;
  janela_meses: number;
  mensagem: string;
}

interface ConfigComum {
  id: number;
  ambiente: number;
  serie: string;
  ultimo_numero: number;
  cert_path: string | null;
  cert_senha: string | null;
}
interface NfceConfig extends ConfigComum {
  csc_id: number | null;
}

export function Fiscal() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [aba, setAba] = useState<Tipo>('nfce');

  // Deep-link ?aba=inutil (banner de buracos de numeracao) abre direto a aba certa.
  useEffect(() => {
    const q = searchParams.get('aba');
    const validas: Tipo[] = ['nfce', 'nfe', 'avaliacao', 'config', 'inutil'];
    if (q && (validas as string[]).includes(q)) setAba(q as Tipo);
  }, [searchParams]);

  const [nfce, setNfce] = useState<NfceConfig | null>(null);
  const [nfe, setNfe] = useState<ConfigComum | null>(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);

  // Estado editavel
  const [nfceForm, setNfceForm] = useState<{ ambiente: number; serie: string; csc_id: string; csc_token: string; cert_senha: string }>({
    ambiente: 2, serie: '020', csc_id: '', csc_token: '', cert_senha: '',
  });
  const [nfeForm, setNfeForm] = useState<{ ambiente: number; serie: string; cert_senha: string }>({
    ambiente: 2, serie: '020', cert_senha: '',
  });
  const [avalForm, setAvalForm] = useState<AvaliacaoConfig>({
    ativo: true, link: '', janela_meses: 6, mensagem: '',
  });
  // Aba "Configuracoes" — aliquota fiscal (% do Simples usada na calculadora de
  // parcelamento e na emissao no valor real). String pra digitacao livre.
  const [aliquota, setAliquota] = useState<string>(String(ALIQUOTA_PADRAO));

  async function carregar() {
    setLoading(true);
    try {
      const [n65, n55, aval, aliq] = await Promise.all([
        api.get<NfceConfig>('/api/nfce/config'),
        api.get<ConfigComum>('/api/nfe/config'),
        api.get<AvaliacaoConfig>('/api/avaliacao-google/config'),
        api.get<{ valor: string | null }>('/api/app-settings/aliquota_fiscal').catch(() => ({ valor: null })),
      ]);
      setNfce(n65);
      setNfe(n55);
      if (aval) setAvalForm(aval);
      if (aliq?.valor) {
        try {
          const j = JSON.parse(aliq.valor) as { aliquota?: number };
          if (j.aliquota != null) setAliquota(String(j.aliquota));
        } catch {
          const n = Number(String(aliq.valor).replace(',', '.'));
          if (Number.isFinite(n)) setAliquota(String(n));
        }
      }
      if (n65) {
        setNfceForm({
          ambiente: n65.ambiente,
          serie: n65.serie,
          csc_id: n65.csc_id !== null ? String(n65.csc_id) : '',
          csc_token: '',
          cert_senha: '',
        });
      }
      if (n55) {
        setNfeForm({
          ambiente: n55.ambiente,
          serie: n55.serie,
          cert_senha: '',
        });
      }
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void carregar(); }, []);

  async function salvarNfce() {
    setSalvando(true);
    try {
      const payload: Record<string, unknown> = {
        ambiente: nfceForm.ambiente,
        serie: nfceForm.serie,
      };
      if (nfceForm.csc_id) payload.csc_id = Number(nfceForm.csc_id);
      if (nfceForm.csc_token) payload.csc_token = nfceForm.csc_token;
      if (nfceForm.cert_senha) payload.cert_senha = nfceForm.cert_senha;
      await api.put('/api/nfce/config', payload);
      toast.sucesso('Configuracao NFC-e salva');
      void carregar();
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  async function salvarNfe() {
    setSalvando(true);
    try {
      const payload: Record<string, unknown> = {
        ambiente: nfeForm.ambiente,
        serie: nfeForm.serie,
      };
      if (nfeForm.cert_senha) payload.cert_senha = nfeForm.cert_senha;
      await api.put('/api/nfe/config', payload);
      toast.sucesso('Configuracao NF-e salva');
      void carregar();
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  async function salvarAvaliacao() {
    setSalvando(true);
    try {
      await api.put('/api/app-settings/avaliacao_google', { valor: JSON.stringify(avalForm) });
      toast.sucesso('Configuracao de avaliacao salva');
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  async function salvarConfig() {
    const n = Number(aliquota.replace(',', '.'));
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      toast.erro('Aliquota deve ser um numero entre 0 e 100');
      return;
    }
    setSalvando(true);
    try {
      await api.put('/api/app-settings/aliquota_fiscal', { valor: JSON.stringify({ aliquota: n }) });
      setAliquota(String(n));
      toast.sucesso('Aliquota fiscal salva');
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  const ambienteAtual = aba === 'nfce' ? nfce?.ambiente : aba === 'nfe' ? nfe?.ambiente : undefined;

  function fechar() {
    navigate('/');
  }
  function salvarAtual() {
    if (aba === 'nfce') void salvarNfce();
    else if (aba === 'nfe') void salvarNfe();
    else if (aba === 'avaliacao') void salvarAvaliacao();
    else void salvarConfig();
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div className="modal-conteudo" style={{ maxWidth: aba === 'inutil' ? 1144 : 880 }}>
        <div className="modal-header">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Receipt size={18} />
            Configuracoes Fiscais
          </span>
          <button type="button" className="btn-fechar-modal" onClick={fechar} aria-label="Fechar">×</button>
        </div>

        <div className="modal-body">
          {ambienteAtual === 1 && (
            <div style={{
              background: '#fee2e2',
              border: '2px solid #dc2626',
              borderRadius: 8,
              padding: 14,
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              color: '#991b1b',
              fontWeight: 700,
            }}>
              <AlertTriangle size={20} />
              AMBIENTE DE PRODUCAO — NOTAS REAIS COM VALOR FISCAL
            </div>
          )}

          <div className="abas">
            <button type="button" className={`aba-btn ${aba === 'nfce' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('nfce')}>
              <Receipt size={14} style={{ marginRight: 4, display: 'inline', marginBottom: -2 }} />
              NFC-e (mod 65)
            </button>
            <button type="button" className={`aba-btn ${aba === 'nfe' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('nfe')}>
              <FileText size={14} style={{ marginRight: 4, display: 'inline', marginBottom: -2 }} />
              NF-e (mod 55)
            </button>
            <button type="button" className={`aba-btn ${aba === 'avaliacao' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('avaliacao')}>
              <Star size={14} style={{ marginRight: 4, display: 'inline', marginBottom: -2 }} />
              Avaliacao Google
            </button>
            <button type="button" className={`aba-btn ${aba === 'config' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('config')}>
              <Settings size={14} style={{ marginRight: 4, display: 'inline', marginBottom: -2 }} />
              Configuracoes
            </button>
            <button type="button" className={`aba-btn ${aba === 'inutil' ? 'aba-btn-ativo' : ''}`} onClick={() => setAba('inutil')}>
              <Eraser size={14} style={{ marginRight: 4, display: 'inline', marginBottom: -2 }} />
              Inutilizacoes
            </button>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
          ) : aba === 'nfce' ? (
            <NfceForm
              form={nfceForm}
              setForm={setNfceForm}
              ultimoNumero={nfce?.ultimo_numero ?? 0}
              certPath={nfce?.cert_path ?? null}
            />
          ) : aba === 'nfe' ? (
            <NfeForm
              form={nfeForm}
              setForm={setNfeForm}
              ultimoNumero={nfe?.ultimo_numero ?? 0}
              certPath={nfe?.cert_path ?? null}
            />
          ) : aba === 'avaliacao' ? (
            <AvaliacaoForm form={avalForm} setForm={setAvalForm} />
          ) : aba === 'config' ? (
            <ConfigForm aliquota={aliquota} setAliquota={setAliquota} />
          ) : (
            <InutilizacoesTab />
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-secundario" onClick={fechar} disabled={salvando}>
            {aba === 'inutil' ? 'Fechar' : 'Cancelar'}
          </button>
          {aba !== 'inutil' && (
            <button type="button" className="btn-primario" onClick={salvarAtual} disabled={salvando || loading}>
              {salvando ? <Loader2 size={14} className="animate-spin" style={{ marginRight: 6, marginBottom: -2 }} /> : <Save size={14} style={{ marginRight: 6, marginBottom: -2 }} />}
              Salvar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface NfceFormProps {
  form: { ambiente: number; serie: string; csc_id: string; csc_token: string; cert_senha: string };
  setForm: (f: NfceFormProps['form']) => void;
  ultimoNumero: number;
  certPath: string | null;
}

function NfceForm({ form, setForm, ultimoNumero, certPath }: NfceFormProps) {
  return (
    <FieldGrid>
      <Linha label="Ambiente">
        <select
          value={form.ambiente}
          onChange={(e) => setForm({ ...form, ambiente: Number(e.target.value) })}
          style={{ maxWidth: 300, padding: '6px 28px 6px 8px', borderRadius: 4, fontSize: 13 }}
        >
          <option value={2}>Homologacao (testes — nao gera valor fiscal)</option>
          <option value={1}>Producao (nota real com valor fiscal)</option>
        </select>
      </Linha>
      <Linha label="Serie">
        <input
          value={form.serie}
          onChange={(e) => setForm({ ...form, serie: e.target.value.replace(/\D/g, '').slice(0, 3) })}
          style={{ maxWidth: 100, padding: '6px 8px', borderRadius: 4, fontSize: 13 }}
          placeholder="020"
        />
      </Linha>
      <Linha label="Ultimo numero" hint="Atualizado automaticamente a cada emissao.">
        <input value={String(ultimoNumero)} readOnly style={{ maxWidth: 120, padding: '6px 8px', borderRadius: 4, fontSize: 13, opacity: 0.7 }} />
      </Linha>
      <Linha label="CSC ID">
        <input
          type="text"
          inputMode="numeric"
          value={form.csc_id}
          onChange={(e) => setForm({ ...form, csc_id: e.target.value.replace(/\D/g, '') })}
          style={{ maxWidth: 120, padding: '6px 8px', borderRadius: 4, fontSize: 13 }}
          placeholder="1"
        />
      </Linha>
      <Linha label="CSC Token" hint="Token gerado no portal SEFAZ-GO. Deixe em branco pra nao alterar.">
        <input
          type="password"
          value={form.csc_token}
          onChange={(e) => setForm({ ...form, csc_token: e.target.value })}
          style={{ padding: '6px 8px', borderRadius: 4, fontSize: 13, maxWidth: 360 }}
          placeholder="•••••••••••••••"
        />
      </Linha>
      <Linha label="Certificado" hint={`Arquivo .pfx no servidor: ${certPath || 'nao configurado'}`}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
          {certPath || '— nao configurado'}
        </span>
      </Linha>
      <Linha label="Senha do certificado" hint="Em branco = mantem senha atual.">
        <input
          type="password"
          value={form.cert_senha}
          onChange={(e) => setForm({ ...form, cert_senha: e.target.value })}
          style={{ padding: '6px 8px', borderRadius: 4, fontSize: 13, maxWidth: 200 }}
          placeholder="•••••"
        />
      </Linha>
    </FieldGrid>
  );
}

interface NfeFormProps {
  form: { ambiente: number; serie: string; cert_senha: string };
  setForm: (f: NfeFormProps['form']) => void;
  ultimoNumero: number;
  certPath: string | null;
}

function NfeForm({ form, setForm, ultimoNumero, certPath }: NfeFormProps) {
  return (
    <FieldGrid>
      <Linha label="Ambiente">
        <select
          value={form.ambiente}
          onChange={(e) => setForm({ ...form, ambiente: Number(e.target.value) })}
          style={{ maxWidth: 300, padding: '6px 28px 6px 8px', borderRadius: 4, fontSize: 13 }}
        >
          <option value={2}>Homologacao (testes — nao gera valor fiscal)</option>
          <option value={1}>Producao (nota real com valor fiscal)</option>
        </select>
      </Linha>
      <Linha label="Serie">
        <input
          value={form.serie}
          onChange={(e) => setForm({ ...form, serie: e.target.value.replace(/\D/g, '').slice(0, 3) })}
          style={{ maxWidth: 100, padding: '6px 8px', borderRadius: 4, fontSize: 13 }}
          placeholder="020"
        />
      </Linha>
      <Linha label="Ultimo numero" hint="Atualizado automaticamente a cada emissao.">
        <input value={String(ultimoNumero)} readOnly style={{ maxWidth: 120, padding: '6px 8px', borderRadius: 4, fontSize: 13, opacity: 0.7 }} />
      </Linha>
      <Linha label="Certificado" hint="NF-e usa o mesmo certificado da NFC-e.">
        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
          {certPath || '— mesmo da NFC-e'}
        </span>
      </Linha>
      <Linha label="Senha do certificado" hint="Em branco = mantem senha atual. Em geral, deixar em branco — usa a mesma da NFC-e.">
        <input
          type="password"
          value={form.cert_senha}
          onChange={(e) => setForm({ ...form, cert_senha: e.target.value })}
          style={{ padding: '6px 8px', borderRadius: 4, fontSize: 13, maxWidth: 200 }}
          placeholder="•••••"
        />
      </Linha>
    </FieldGrid>
  );
}

function AvaliacaoForm({ form, setForm }: { form: AvaliacaoConfig; setForm: (f: AvaliacaoConfig) => void }) {
  return (
    <FieldGrid>
      <Linha label="Pedir avaliacao" hint="Quando ligado, o modal de enviar a NF mostra a opcao de pedir avaliacao no Google.">
        <select
          value={form.ativo ? '1' : '0'}
          onChange={(e) => setForm({ ...form, ativo: e.target.value === '1' })}
          style={{ maxWidth: 220, padding: '6px 28px 6px 8px', borderRadius: 4, fontSize: 13 }}
        >
          <option value="1">Ligado</option>
          <option value="0">Desligado</option>
        </select>
      </Linha>
      <Linha label="Link de avaliacao" hint="Link curto do Google Meu Negocio — botao 'Solicitar avaliacoes' no painel do perfil.">
        <input
          value={form.link}
          onChange={(e) => setForm({ ...form, link: e.target.value })}
          className="no-upper"
          style={{ padding: '6px 8px', borderRadius: 4, fontSize: 13, width: '100%', maxWidth: 440, textTransform: 'none' }}
          placeholder="https://g.page/r/.../review"
        />
      </Linha>
      <Linha label="Repetir a cada (meses)" hint="Nao pede avaliacao de novo pro mesmo cliente antes desse prazo.">
        <input
          type="number"
          min={1}
          value={form.janela_meses}
          onChange={(e) => setForm({ ...form, janela_meses: Math.max(1, Number(e.target.value) || 1) })}
          style={{ maxWidth: 100, padding: '6px 8px', borderRadius: 4, fontSize: 13 }}
        />
      </Linha>
      <Linha label="Mensagem" hint="Use {nome} pro primeiro nome do cliente e {link} pro link de avaliacao.">
        <textarea
          value={form.mensagem}
          onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
          rows={4}
          className="no-upper"
          style={{ padding: '6px 8px', borderRadius: 4, fontSize: 13, width: '100%', maxWidth: 480, resize: 'vertical', textTransform: 'none' }}
          placeholder="Oi {nome}! Obrigado por comprar na FIODECOBRE..."
        />
      </Linha>
    </FieldGrid>
  );
}

function ConfigForm({ aliquota, setAliquota }: { aliquota: string; setAliquota: (v: string) => void }) {
  return (
    <FieldGrid>
      <Linha
        label="Aliquota Fiscal (%)"
        hint="Aliquota efetiva do Simples informada pelo contador. Usada pra calcular o imposto a mais no parcelamento no cartao (a partir do 2x) e pra emitir a nota no valor real. Editavel sem novo deploy."
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="text"
            inputMode="decimal"
            value={aliquota}
            onChange={(e) => setAliquota(e.target.value.replace(/[^\d.,]/g, '').slice(0, 6))}
            style={{ maxWidth: 100, padding: '6px 8px', borderRadius: 4, fontSize: 13, textAlign: 'right' }}
            placeholder="15"
          />
          <span style={{ fontSize: 14, fontWeight: 600 }}>%</span>
        </div>
      </Linha>
    </FieldGrid>
  );
}

function FieldGrid({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gap: 14 }}>{children}</div>;
}

interface LinhaProps { label: string; hint?: string; children: React.ReactNode }
function Linha({ label, hint, children }: LinhaProps) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 12, alignItems: 'flex-start' }}>
      <label style={{ fontSize: 12, fontWeight: 600, paddingTop: 6 }}>{label}</label>
      <div>
        {children}
        {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{hint}</div>}
      </div>
    </div>
  );
}
